import React, { useEffect, useState, useContext, createRef } from 'react';
import PropTypes from 'prop-types';
import HeaderMenuContext from 'utils/HeaderMenuContext';
import useIsScrolled from 'hooks/useIsScrolled';
import useGlobalNavigation from 'hooks/useGlobalNavigation';
import useWindowDimensions from 'hooks/useWindowDimensions';
import HeaderLogo from './HeaderLogo';
import MenuItems from './MenuItems/index';
import WebsiteHeaderRightMenu from './WebsiteHeaderRightMenu';
import AnnouncementBar from './AnnouncementBar/index';
import AnnouncementBarContext from 'utils/AnnouncementBarContext';

import { WebsiteHeaderContainer, WebsiteHeaderInnerContainer } from './styles';

const WebsiteHeader = ({
  announcementBar,
  absolutePosition,
  background,
  contextualCta,
  customPhoneNumber,
  hasAnchorLinkNavigation,
  landingPage,
  landingPageMenuItems,
  logoRoute,
  logoType,
  navigation,
  showMainMenuItems,
  stickyHeader,
  noHero,
  theme,
  mobileUseGlobalNav,
}) => {
  const { navigationMenuUrls, setNavigationMenuUrls } = useContext(HeaderMenuContext);
  const [isMobileMenuOpened, setMobileMenuOpened] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { isScrolled: announcementBarScroll } = useIsScrolled();
  const { isScrolled: shrinkNavScroll } = useIsScrolled(300);
  const [scrollPosY, setScrollPosY] = useState(0);
  const windowWidth = useWindowDimensions().innerWidth;
  const landingPageWithoutItems = landingPage && !landingPageMenuItems;
  const showMenuItems = showMainMenuItems || !landingPage;
  const headerBody = createRef();
  const globalNavigation = useGlobalNavigation()?.navigation;
  const navigationMenu = [
    ...((windowWidth <= 1240 && mobileUseGlobalNav) || !navigation?.navigation
      ? globalNavigation
      : navigation?.navigation),
  ];
  const backgroundColor = theme === 'simpleHeader' ? 'white' : background;
  const { hideAnnouncementBar } = useContext(AnnouncementBarContext);

  useEffect(() => {
    if (announcementBarScroll) setHasScrolled(true);
  }, [announcementBarScroll]);

  useEffect(() => {
    if (isMobileMenuOpened === true) {
      const { scrollY } = window;
      setScrollPosY(scrollY);
      document.body.style.position = 'fixed';
    } else {
      document.body.style.position = '';
      window.scrollTo(0, parseInt(scrollPosY || '0'));
    }
  }, [isMobileMenuOpened]);

  useEffect(() => {
    if (!navigationMenuUrls && setNavigationMenuUrls) {
      const menuUrls = globalNavigation?.map(main =>
        (main.subNavigationElement || []).map(item => item.url)
      );
      setNavigationMenuUrls(menuUrls);
    }
  }, []);

  navigationMenu.push(
    {
      type: 'menu mobile-view',
      icon: null,
      label: 'Sign in',
      url: 'https://go.servicetitan.com/Auth/Login',
      subNavigationElement: null,
    },
    {
      type: 'menu mobile-view',
      icon: null,
      label: customPhoneNumber || '(855) 899-0970',
      url: `tel: ${customPhoneNumber || '(855) 899-0970'}`,
      subNavigationElement: null,
    }
  );

  const headerScrollClassName =
    theme === 'simpleHeader' || theme === 'marketingPro' || hasAnchorLinkNavigation
      ? ''
      : announcementBarScroll
      ? `scrolled ${shrinkNavScroll ? 'scrolled-2' : ''}`
      : (hasScrolled && !announcementBarScroll) || noHero
      ? 'top'
      : '';

  return (
    <WebsiteHeaderContainer
      $hideAnnouncementBar={hideAnnouncementBar}
      theme={'lightHeroHeader'}
      scrolled={announcementBarScroll && stickyHeader && theme !== 'simpleHeader'}
      mobileMenuOpen={isMobileMenuOpened}
      ref={headerBody}
      className={`website-header${
        isMobileMenuOpened ? ' mobile-menu-opened' : ''
      } ${headerScrollClassName}`}
      background={backgroundColor}
      data-testid="website-header"
    >
      <div className="website-header--main-menu">
        {!hideAnnouncementBar && <AnnouncementBar theme={theme} {...announcementBar} />}
        <WebsiteHeaderInnerContainer className="website-header--inner-container">
          <HeaderLogo route={logoRoute} type={logoType} />
          {showMenuItems && (
            <MenuItems
              navigationMenu={navigationMenu}
              isMobileMenuOpened={isMobileMenuOpened}
              setMobileMenuOpened={setMobileMenuOpened}
            />
          )}
          <WebsiteHeaderRightMenu
            absolutePosition={absolutePosition}
            contextualCta={contextualCta}
            customPhoneNumber={customPhoneNumber}
            isMobileMenuOpened={isMobileMenuOpened}
            landingPageMenuItems={landingPageMenuItems}
            landingPageWithoutItems={landingPageWithoutItems}
            setMobileMenuOpened={setMobileMenuOpened}
            showMenuItems={showMenuItems}
            theme={theme}
          />
        </WebsiteHeaderInnerContainer>
      </div>
    </WebsiteHeaderContainer>
  );
};

WebsiteHeader.defaultProps = {
  stickyHeader: true,
  showMainMenuItems: true,
};

WebsiteHeader.propTypes = {
  background: PropTypes.string,
  contextualCta: PropTypes.object,
  absolutePosition: PropTypes.bool,
  landingPage: PropTypes.bool,
  landingPageMenuItems: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  logoRoute: PropTypes.string,
  navigation: PropTypes.object,
  showMainMenuItems: PropTypes.bool,
  simpleHeader: PropTypes.bool,
  stickyHeader: PropTypes.bool,
};

export default WebsiteHeader;
